<template>
  <Layout>
    <div>
      <PageHeader :title="title" />
      <p class="mb-4">
        {{ $t('master.address.description') }}
      </p>

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex">
          <McButtonBack href="/master/" />
        </div>
      </div>
    </div>
    <b-card class="mc-card__setting">
      <b-tabs content-class="p-3 text-muted">
        <b-tab
          :active="selectedTab === 'company'"
          :title-item-class="
            !hasAccessAll(['tms:read', 'geofences:read', 'd3a-default-mdalamat:read'])
              ? 'd-none'
              : ''
          "
          @click="refreshTable('company')"
        >
          <template #title>
            <span class="d-none d-sm-inline-block">
              {{ $t('master.list.address.company') }}
            </span>
          </template>
          <div v-if="!selectedTab">
            <b-skeleton width="5%" height="40px" class="mb-5 float-end" />
            <b-skeleton width="100%" height="300px" class="my-5" />
          </div>
          <McGrapqlTable
            v-show="selectedTab === 'company'"
            :apiClient="query"
            :fields="companyFields"
            :customHandlers="customHandlers"
            :hasCheckbox="true"
            :searchable="true"
            :withCard="false"
            :stickyHeader="false"
            trackBy="geofenceId"
            isTextWrap
            :refreshData="!loading"
            :isEmpty="noData"
            :isLoading="isLoading"
          >
            <template #custom-btn>
              <McButton
                variant="secondary"
                :text="$t('global.action.upload')"
                icon="icon-outline_upload"
                class="me-2 mb-2"
                @click="handleCompanyUpload"
              />
            </template>
            <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
              <McFilterSelect
                :defaultColumnOrder="initialFields"
                :currentColumnOrder="dataFields"
                @setColumn="handleSetDataFields"
                @setField="handleFieldShow"
              />
            </template>
            <template #emptyState>
              <McEmptyState
                :title="$t('maintenance.nolist.noData')"
                :desc="$t('master.service.emptyState.desc')"
                class="pb-4"
                @buttonPrimaryAction="customHandlers.handleAdd"
              />
            </template>
          </McGrapqlTable>
        </b-tab>

        <b-tab
          v-if="hasAccessAll(['tms:read', 'geofences:read', 'd3a-default-mdalamat:read'])"
          :active="selectedTab === 'customer'"
          @click="refreshTable('customer')"
        >
          <template #title>
            <span class="d-none d-sm-inline-block">
              {{ $t('master.list.address.customer') }}
            </span>
          </template>

          <div v-if="!selectedTab">
            <b-skeleton width="5%" height="40px" class="mb-5 float-end" />
            <b-skeleton width="100%" height="300px" class="my-5" />
          </div>
          <McGrapqlTable
            v-show="selectedTab === 'customer'"
            :apiClient="query"
            :fields="customerfields"
            :customHandlers="customHandlers"
            :hasCheckbox="true"
            :searchable="true"
            :isSelectableField="false"
            :withCard="false"
            :stickyHeader="false"
            trackBy="geofenceId"
            isTextWrap
            :refreshData="!loading"
            :isEmpty="noData"
            :isLoading="isLoading"
          >
            <template #custom-btn>
              <McButton
                variant="secondary"
                :text="$t('global.action.upload')"
                icon="icon-outline_upload"
                class="me-2 mb-2"
                @click="handleCustomerUpload"
              />
            </template>
            <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
              <McFilterSelect
                :defaultColumnOrder="initialFields"
                :currentColumnOrder="dataFields"
                @setColumn="handleSetDataFields"
                @setField="handleFieldShow"
              />
            </template>
            <template #emptyState>
              <McEmptyState
                :title="$t('maintenance.nolist.noData')"
                :desc="$t('master.service.emptyState.desc')"
                class="pb-4"
                @buttonPrimaryAction="customHandlers.handleAdd"
              />
            </template>
          </McGrapqlTable>
        </b-tab>
      </b-tabs>
    </b-card>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McEmptyState from '@/components/McEmptyState'
import { GET_ADDRESS, DELETE_ADDRESS } from '@/services/api/master/address'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Master',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McEmptyState,
  },
  props: {
    status: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      customHandlers: {
        handleAdd: () => {
          return this.$router.push({ name: 'Master Address Create' })
        },
        handleClickRow: (item) => {
          return this.$router.push({
            name: 'Master Address Update',
            params: { id: item.geofenceId },
          })
        },
      },
      companyFieldShow: JSON.parse(localStorage.getItem('companyAddressFields')),
      customerFieldShow: JSON.parse(localStorage.getItem('customerAddressFields')),
      loading: true,
      noData: true,
      isLoading: true,
      localSelectedTab: 'company',
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    selectedTab: {
      get() {
        return this.$route.query.tab || 'company'
      },
      set(value) {
        this.localSelectedTab = value
      },
    },
    title() {
      return this.$t('menuitems.masterData.list.address')
    },
    query() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            query: GET_ADDRESS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                isCustomer: this.selectedTab == 'customer' ? true : false,
              },
            },
          })

          if (res.data.geofences.geofences.pagination.total === 0) this.noData = true
          else this.noData = false
          this.isLoading = false

          return res.data.geofences.geofences
        },

        delete: async (id) => {
          const res = await this.$apollo.query({
            query: DELETE_ADDRESS,
            fetchPolicy: 'no-cache',
            variables: {
              input: {
                geofenceId: Number(id),
              },
            },
          })

          if (!res.data.deleteGeofence.isSuccessful) {
            const errorMessage = res.data.deleteGeofence.message
            if (res.data.deleteGeofence.status == 400) {
              if (errorMessage.includes('is using in')) {
                const address = errorMessage.substring(
                  errorMessage.indexOf('Address '),
                  errorMessage.indexOf(' is')
                )

                const startIndex = errorMessage.indexOf(' in ')
                let location = errorMessage.substring(startIndex + 4)
                if (location.endsWith('"') || location.endsWith("'")) {
                  location = location.slice(0, -1)
                }

                this.$mcToast.error(
                  this.$t('master.address.geofenceBeingUsed', {
                    address: address,
                    location: location,
                  })
                )
              } else if (errorMessage.includes('Geofence is being used in a Voice Announcer')) {
                this.$mcToast.error(this.$t('master.form.address.enterVA'))
              } else {
                this.$mcToast.error(this.$t('master.address.deleteFailed'))
              }
            } else {
              this.$mcToast.error(this.$t('master.address.deleteFailed'))
            }
          }

          return res.data.deleteGeofence.isSuccessful
        },
      }
    },
    tabOptions() {
      let tab = []
      if (this.hasAccessAll(['tms:read', 'geofences:read', 'd3a-default-mdalamat:read'])) {
        tab = [
          {
            text: this.$t('master.list.address.company'),
            value: 'company',
          },
          {
            text: this.$t('master.list.address.customer'),
            value: 'customer',
          },
        ]
      }
      return tab
    },
    companyStoredFields() {
      return this.companyFieldShow.column
    },
    companyFields() {
      if (this.companyFieldShow) {
        return this.companyStoredFields
      } else {
        return [
          {
            key: 'fenceName',
            label: this.$t('master.form.address.name'),
            thClass: 'mc-table--150',
            isRedirectable: true,
            sortable: true,
            isLocked: true,
          },
          {
            key: 'category',
            label: this.$t('master.form.address.category'),
            sortable: true,
          },
          {
            key: 'address',
            label: this.$t('master.form.address.fullAddress'),
            sortable: true,
            isShortable: true,
            shortableLength: 255,
          },
          {
            key: 'province',
            label: this.$t('master.list.address.province'),
            sortable: true,
          },
          {
            key: 'city',
            label: this.$t('master.list.address.city'),
            sortable: true,
          },
        ]
      }
    },
    customerStoredFields() {
      return this.customerFieldShow.column
    },
    customerfields() {
      if (this.customerFieldShow) {
        return this.customerStoredFields
      } else {
        return [
          {
            key: 'fenceName',
            label: this.$t('master.form.address.name'),
            thClass: 'mc-table--170',
            isRedirectable: true,
            sortable: true,
            isLocked: true,
          },
          {
            key: 'customerName',
            label: this.$t('master.form.address.customer'),
            sortable: true,
          },
          {
            key: 'category',
            label: this.$t('master.form.address.category'),
            sortable: true,
          },
          {
            key: 'address',
            label: this.$t('master.form.address.fullAddress'),
            sortable: true,
            isShortable: true,
            shortableLength: 255,
          },
          {
            key: 'province',
            label: this.$t('master.list.address.province'),
            sortable: true,
          },
          {
            key: 'city',
            label: this.$t('master.list.address.city'),
            sortable: true,
          },
        ]
      }
    },
  },
  mounted() {
    if (!this.$route.query.tab || !['company', 'customer'].includes(this.$route.query.tab)) {
      this.$router.replace({
        name: this.$route.name,
        query: { ...this.$route.query, tab: 'company' },
      })
    }

    this.$store.commit('masterAddress/SET_STATUS', this.selectedTab)
  },
  methods: {
    handleFieldShow(fields) {
      let savedAs = 'companyAddressFields'
      if (this.selectedTab == 'customer') {
        savedAs = 'customerAddressFields'
      }
      localStorage.setItem(savedAs, JSON.stringify({ column: fields }))
    },
    refreshTable(value) {
      this.loading = true

      const currentTab = this.$route.query.tab || 'company'
      if (currentTab !== value) {
        this.$router.replace({
          name: this.$route.name,
          query: { ...this.$route.query, tab: value },
        })
      }

      setTimeout(() => {
        this.$store.commit('masterAddress/SET_STATUS', value)
        this.loading = false
      }, 500)
    },
    handleCompanyUpload() {
      this.$router.push({
        name: 'Master Address Import',
        query: { type: 'company' },
      })
    },
    handleCustomerUpload() {
      this.$router.push({
        name: 'Master Address Import',
        query: { type: 'customer' },
      })
    },
    handleUploadNoData() {
      if (this.selectedTab == 'company') {
        this.handleCompanyUpload()
      } else {
        this.handleCustomerUpload()
      }
    },
  },
}
</script>
